import {
  availabilitiesAtom,
  categoriesAtom,
  editingItemAtom,
  itemModalStepAtom,
  itemOffersAtom,
  itemOptionsQuantitiesAtom,
  mandatoryOptionGroupsAtom,
  optionSpecialInstructionsAtom,
} from "@/atoms";
import { indoorCategoriesAtom, indoorItemOffersAtom } from "@/atoms/indoor";
import { makeItemOfferDetailAtoms } from "@/atoms/make-item-offer-detail-atoms";
import { tableAtom } from "@/atoms/table";
import { minuteNowAtom, tenantTimezoneAtom } from "@/atoms/tenant-clock";
import { closeSearchAtom } from "@/atoms/search";
import { AdditionalInfo, BottomButtons, OptionGroupCard } from "@/components/item";
import { ItemDetailSkeleton } from "@/components/item/ItemDetailSkeleton";
import { ItemHero } from "@/components/item/ItemHero";
import { ItemStickyTitleBar } from "@/components/item/ItemStickyTitleBar";
import { ItemTitleBlock } from "@/components/item/ItemTitleBlock";
import { ItemUnavailableState } from "@/components/item/ItemUnavailableState";
import { ImageModal } from "@/components/modals";
import { ItemSuggestionsStrip } from "@/components/suggestions/ItemSuggestionsStrip";
import { recordStorefrontEvent } from "@/domain/storefront-analytics";
import { Button, FixedFooter, PageContainer, SegmentedProgress, Text } from "@/components/ui";
import { DURATION } from "@/lib/motion";
import { useOptimisticCart } from "@/hooks/use-optimistic-cart";
import { ItemOfferDisplayMode } from "@/types/item-offer";
import { isOfferVisibleNow } from "@/lib/catalog-visibility";
import { trackMetaViewContent } from "@/lib/meta-pixel";
import { smoothScrollToElement } from "@/lib/smooth-scroll-to-element";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

interface ItemModalContentProps {
  onClose: () => void;
  itemOfferId: string;
  /** Scroll container of the modal — drives the auto-scroll between groups. */
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
}

export function ItemModalContent({ onClose, itemOfferId, scrollContainerRef }: ItemModalContentProps) {
  const table = useAtomValue(tableAtom);
  const { categoryAtom, itemOfferAtom, itemOfferOptionGroupsAtom, itemAtom, minimumOfferPriceAtom } = useMemo(
    () =>
      makeItemOfferDetailAtoms(itemOfferId, {
        categoriesAtom: table ? indoorCategoriesAtom : categoriesAtom,
        itemOffersAtom: table ? indoorItemOffersAtom : itemOffersAtom,
      }),
    [itemOfferId, table]
  );

  const setMandatoryOptionGroups = useSetAtom(mandatoryOptionGroupsAtom);
  const category = useAtomValue(categoryAtom);
  const itemOffer = useAtomValue(itemOfferAtom);
  const optionGroups = useAtomValue(itemOfferOptionGroupsAtom);
  const item = useAtomValue(itemAtom);
  const minimumOfferPrice = useAtomValue(minimumOfferPriceAtom);
  const mandatoryOptionGroupsState = useAtomValue(mandatoryOptionGroupsAtom);
  const availabilities = useAtomValue(availabilitiesAtom);
  const timeZone = useAtomValue(tenantTimezoneAtom);
  const now = useAtomValue(minuteNowAtom)
  const closeSearch = useSetAtom(closeSearchAtom);
  const { cart } = useOptimisticCart();

  const [specialInstructions, setSpecialInstructions] = useState("");
  const [isImageVisible, setIsImageVisible] = useState(true);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [displayMode, setDisplayMode] = useState<ItemOfferDisplayMode>(ItemOfferDisplayMode.LIST);
  const [currentStep, setCurrentStep] = useAtom(itemModalStepAtom);
  const imageRef = useRef<HTMLDivElement>(null);
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const groupRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const prevMandatoryRef = useRef<Record<string, boolean>>({});
  const [autoScrollArmed, setAutoScrollArmed] = useState(false);

  const setItemOptionsQuantities = useSetAtom(itemOptionsQuantitiesAtom);
  const setOptionSpecialInstructions = useSetAtom(optionSpecialInstructionsAtom);
  const setEditingItem = useSetAtom(editingItemAtom);

  const suitableDiets = item?.nutritionalInfo?.suitableDiets;

  useEffect(() => {
    setEditingItem(true);
    return () => setEditingItem(false);
  }, [setEditingItem]);

  const viewContentFired = useRef(false);
  useEffect(() => {
    if (viewContentFired.current || !item) return;
    viewContentFired.current = true;
    trackMetaViewContent(item, minimumOfferPrice ?? undefined);
    recordStorefrontEvent({ type: "item_view", itemId: item.id, itemName: item.name });
  }, [item, minimumOfferPrice]);

  const [atomsReset, setAtomsReset] = useState(false);
  useLayoutEffect(() => {
    setItemOptionsQuantities({});
    setMandatoryOptionGroups({});
    setOptionSpecialInstructions({});
    setCurrentStep(0);
    setAtomsReset(true);
  }, [setItemOptionsQuantities, setMandatoryOptionGroups, setOptionSpecialInstructions, setCurrentStep]);

  useEffect(() => {
    closeSearch();
  }, [closeSearch]);

  useEffect(() => {
    if (itemOffer?.displayMode) {
      const mode =
        itemOffer.displayMode === ItemOfferDisplayMode.LIST
          ? ItemOfferDisplayMode.LIST
          : ItemOfferDisplayMode.STEP_BY_STEP;
      setDisplayMode(mode);
    }
  }, [itemOffer?.displayMode]);

  useEffect(() => {
    setAutoScrollArmed(false);
    prevMandatoryRef.current = {};
    const timer = setTimeout(() => setAutoScrollArmed(true), 500);
    return () => clearTimeout(timer);
  }, [itemOfferId]);

  useEffect(() => {
    const prev = prevMandatoryRef.current;
    prevMandatoryRef.current = mandatoryOptionGroupsState;

    if (displayMode !== ItemOfferDisplayMode.LIST || !autoScrollArmed) return;

    const justCompleted = optionGroups.find(
      (group) => prev[group.id] === false && mandatoryOptionGroupsState[group.id] === true
    );
    if (!justCompleted) return;

    const completedIndex = optionGroups.findIndex((group) => group.id === justCompleted.id);
    const nextPending = optionGroups
      .slice(completedIndex + 1)
      .find((group) => mandatoryOptionGroupsState[group.id] === false);
    if (!nextPending) return;

    const target = groupRefs.current[nextPending.id];
    if (!target) return;

    requestAnimationFrame(() => {
      smoothScrollToElement(target, {
        offset: 80,
        duration: DURATION.emphasis * 1000,
        container: scrollContainerRef?.current ?? null,
      });
    });
  }, [mandatoryOptionGroupsState, displayMode, autoScrollArmed, optionGroups, scrollContainerRef]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsImageVisible(entry.intersectionRatio > 0.1);
      },
      {
        threshold: [0, 0.1, 0.5, 1],
        rootMargin: "-50px 0px -50px 0px",
      }
    );

    const imageElement = imageRef.current;
    if (imageElement) {
      observer.observe(imageElement);
    }

    return () => {
      if (imageElement) {
        observer.unobserve(imageElement);
      }
    };
  }, [atomsReset, item?.id]);

  useEffect(() => {
    setMandatoryOptionGroups(
      optionGroups.reduce((acc, opg) => {
        if (opg.minPermitted > 0) return { ...acc, [opg.id]: false };
        else return { ...acc, [opg.id]: true };
      }, {})
    );
  }, [optionGroups, itemOffer, setMandatoryOptionGroups]);

  useEffect(() => {
    if (!category || !itemOffer || !item) {
      if (!redirectTimerRef.current) {
        redirectTimerRef.current = setTimeout(() => {
          window.location.href = "/";
        }, 1000);
      }
    } else {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
        redirectTimerRef.current = null;
      }
    }
  }, [category, itemOffer, item]);

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  const isCurrentStepValid = useMemo(() => {
    if (displayMode !== ItemOfferDisplayMode.STEP_BY_STEP) return true;
    if (currentStep >= optionGroups.length) return true;

    const currentGroup = optionGroups[currentStep];
    if (!currentGroup) return true;

    if (currentGroup.minPermitted === 0) return true;

    return mandatoryOptionGroupsState[currentGroup.id] === true;
  }, [displayMode, currentStep, optionGroups, mandatoryOptionGroupsState]);

  if (!itemOfferId) {
    return (
      <div className="flex items-center justify-center h-64">
        <Text tone="muted">Item não encontrado</Text>
      </div>
    );
  }

  if (!category || !itemOffer || !item) return <ItemDetailSkeleton />;

  if (!isOfferVisibleNow(itemOffer, availabilities, category, { now, timeZone })) {
    return <ItemUnavailableState onClose={onClose} />;
  }

  const handleImageClick = () => {
    if (item.image?.URL) {
      setIsImageModalOpen(true);
    }
  };

  const hasAdditionalInfoStep = itemOffer?.allowsSpecialInstructions !== false;
  const totalSteps =
    displayMode === ItemOfferDisplayMode.STEP_BY_STEP ? optionGroups.length + (hasAdditionalInfoStep ? 1 : 0) : 0;
  const isLastStep = currentStep === totalSteps - 1;

  const handleNextStep = () => {
    if (currentStep < totalSteps - 1 && isCurrentStepValid) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  if (!atomsReset) return null;

  return (
    <div className="mx-auto max-w-shell pb-32">
      <div className="flex flex-col">
        <ItemHero
          containerRef={imageRef}
          name={item.name}
          imageUrl={item.image?.URL}
          imageCrc={item.image?.["CRC-32"]}
          onZoom={handleImageClick}
          isPromotional={itemOffer.isPromotional || category.isPromotional}
          promotionalText={itemOffer.promotionalBannerText || category.promotionalBannerText}
          flyOrigin
        />

        <ItemStickyTitleBar title={item.name} onClose={onClose} imageVisible={isImageVisible} />

        <ItemTitleBlock
          key={itemOfferId}
          name={item.name}
          description={item.description}
          itemOffer={itemOffer}
          minimumOfferPrice={minimumOfferPrice}
          suitableDiets={suitableDiets}
        />

        <div className="pt-4">
          {displayMode === ItemOfferDisplayMode.STEP_BY_STEP && totalSteps > 1 && (
            <div className="mb-2 w-full">
              <SegmentedProgress variant="dots" segments={totalSteps} current={currentStep} label="Progresso do item" />
              <Text size="sm" tone="muted" align="center" className="mt-2">
                Passo {currentStep + 1} de {totalSteps}
              </Text>
            </div>
          )}
          {displayMode === ItemOfferDisplayMode.STEP_BY_STEP ? (
            <>
              {currentStep < optionGroups.length && (
                <OptionGroupCard
                  key={optionGroups[currentStep].id}
                  optionGroup={optionGroups[currentStep]}
                  step={currentStep + 1}
                  everyStep={optionGroups.length}
                  displayMode={displayMode}
                />
              )}
              {currentStep === optionGroups.length && itemOffer?.allowsSpecialInstructions !== false && (
                <AdditionalInfo
                  specialInstructions={specialInstructions}
                  setSpecialInstructionsAction={setSpecialInstructions}
                />
              )}
            </>
          ) : (
            <>
              {optionGroups.map((optionGroup, index) => (
                <div
                  key={optionGroup.id}
                  ref={(el) => {
                    groupRefs.current[optionGroup.id] = el;
                  }}
                  className="scroll-mt-20"
                >
                  <OptionGroupCard optionGroup={optionGroup} step={index + 1} everyStep={optionGroups.length} />
                </div>
              ))}

              {itemOffer?.allowsSpecialInstructions !== false && (
                <AdditionalInfo
                  specialInstructions={specialInstructions}
                  setSpecialInstructionsAction={setSpecialInstructions}
                />
              )}
            </>
          )}
        </div>
      </div>

      {(displayMode !== ItemOfferDisplayMode.STEP_BY_STEP || isLastStep) && (
        <PageContainer>
          <ItemSuggestionsStrip
            viewedItemId={item.id}
            cartItemIds={cart.items.map((line) => line.itemOffer.itemId)}
          />
        </PageContainer>
      )}

      {displayMode === ItemOfferDisplayMode.STEP_BY_STEP && !isLastStep ? (
        <FixedFooter>
          <div className="flex items-stretch gap-2 sm:gap-3">
            {currentStep > 0 && (
              <Button
                variant="outline"
                tone="neutral"
                onClick={handlePreviousStep}
                className="shrink-0 basis-1/3 whitespace-nowrap"
              >
                Voltar
              </Button>
            )}
            <Button onClick={handleNextStep} disabled={!isCurrentStepValid} className="flex-1 whitespace-nowrap">
              Avançar
            </Button>
          </div>
        </FixedFooter>
      ) : (
        <BottomButtons
          item={item}
          itemOffer={itemOffer}
          optionGroups={optionGroups}
          specialInstructions={specialInstructions}
          category={category}
        />
      )}

      <ImageModal
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        imageUrl={item.image?.URL}
        crc={item.image?.["CRC-32"]}
        alt={item.name}
      />
    </div>
  );
}
